import { Spinner, Flex, Box } from '@chakra-ui/react';
import { OfferDetails } from './components/OfferDetails';
import { OfferLetterTemplate } from './components/OfferLetterTemplate';
import { OfferApproval } from './components/OfferApproval';
import { OfferEmail } from './components/offer-email/OfferEmail';
import { EsignEmailForm } from './components/offer-email/EsignEmailForm';
import { useMemo, useEffect, useRef } from 'react';
import { useOfferInitiationConfig } from '@/components/ats/OfferInitiation/useOfferInitiationConfig';
import {
  useGetCustomFieldsQuery,
  useGetCompensationCurrenciesQuery,
  useGetAtsOfficesQuery,
  useGetAtsDepartmentsQuery,
  useGetOfferByIdQuery,
} from './api';
import { getInitializeOfferValues } from '@/components/ats/OfferInitiation/offerFormHelpers';
import { getAtsJobCreate } from '@/store/atsJobCreate';
import { useSelector } from 'react-redux';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { useApplicationHelper } from '@/components/candidate-job-slider-v1/hooks/useApplicationHelper';
import { useOfferStore } from './store';
import { useKFlags } from '@/hooks/use-k-flags';
import { nextSeededAttachments, closeStaleDatePicker } from './helpers';

interface OfferBodyContainerProps {
  currentStep: any;
  childRefs: any;
  onTemplateSelect?: (_templateId: string, _templateValue: any) => Promise<any>;
  type?: 'create' | 'edit';
  sendMode?: boolean;
  onEditOffer?: (_tokens?: string[]) => void;
  reapprovalHighlightTokens?: string[];
}

export const OfferBodyContainer = ({
  currentStep,
  childRefs,
  onTemplateSelect,
  type,
  sendMode = false,
  onEditOffer,
  reapprovalHighlightTokens = [],
}: OfferBodyContainerProps) => {
  const { applicationData: applicantData, isLoading: isApplicantFetching } =
    useApplicationHelper();

  const { currentJob = {} } = useSelector(getAtsJobCreate);
  const currUser = useSelector(selectCurrUser);
  const detailsFormRef = useRef(null);
  const offerId = useOfferStore((state) => state.offerId);
  const setOfferId = useOfferStore((state) => state.setOfferId);
  const signatureType = useOfferStore((state) => state.signatureType);
  const setOfferAttachments = useOfferStore(
    (state) => state.setOfferAttachments
  );
  const offerAttachments = useOfferStore((state) => state.offerAttachments);
  const flags = useKFlags();
  const multiDocsEnabled = !!flags.tOfferTemplateMultiDocuments;

  // Fetch offer data if offerId is provided (for editing existing offer)
  const { data: currentOffer, isFetching: isOfferFetching } =
    useGetOfferByIdQuery(offerId, { staleTime: 0 });

  // Fetch custom fields data
  const { data: customFieldsData, isFetching: isCustomFieldsLoading } =
    useGetCustomFieldsQuery({ subject_type: 'ats_offer' }, { enabled: true });

  const { isFetching: isCurrenciesLoading } =
    useGetCompensationCurrenciesQuery();

  const { isFetching: isOfficesLoading } = useGetAtsOfficesQuery();

  const { isFetching: isDepartmentsLoading } = useGetAtsDepartmentsQuery();

  // Extract data from responses
  const offerStaticFields = useMemo(() => {
    return customFieldsData?.ats_form_template?.static_fields || [];
  }, [customFieldsData]);

  // Extract default values for additional compensation section
  const additionalCompensationDefaults = useMemo(() => {
    const additionalCompSection =
      customFieldsData?.ats_form_template?.static_fields?.find(
        (section) => section.id === 'additional_compensation'
      );
    return additionalCompSection?.default || {};
  }, [customFieldsData]);

  const rowIdToCfIdMap = useMemo(() => {
    return (customFieldsData?.ats_form_fields || []).reduce(
      (acc: Record<string, string>, f: any) => {
        if (
          f?.ats_custom_field_id !== null &&
          f?.ats_custom_field_id !== undefined &&
          f?.id !== null &&
          f?.id !== undefined
        ) {
          acc[String(f.id)] = String(f.ats_custom_field_id);
        }
        return acc;
      },
      {} as Record<string, string>
    );
  }, [customFieldsData]);

  // Compute initialValues synchronously using useMemo to avoid race condition
  const initialValues = useMemo(() => {
    return getInitializeOfferValues({
      currentOffer,
      contactData: applicantData?.contact,
      currentJob: currentJob?.data,
      currUser,
      type,
      additionalCompensationDefaults,
      rowIdToCfIdMap,
    });
  }, [
    currentOffer,
    applicantData,
    currentJob?.data,
    currUser,
    type,
    additionalCompensationDefaults,
    rowIdToCfIdMap,
  ]);

  // Fill-source for send-mode token validation: the offer's raw values WITHOUT the
  // FE structural defaults (currency ids → 5, vesting units → year/month). Using the
  // defaults-laden initialValues made a template switch read its default-backed
  // required fields as already filled, so validation didn't re-prompt (CORE-4822).
  const validationValues = useMemo(() => {
    if (!sendMode) return undefined;
    return getInitializeOfferValues({
      currentOffer,
      contactData: applicantData?.contact,
      currentJob: currentJob?.data,
      currUser,
      type,
      additionalCompensationDefaults,
      rowIdToCfIdMap,
      applyStructuralDefaults: false,
    }).details;
  }, [
    sendMode,
    currentOffer,
    applicantData,
    currentJob?.data,
    currUser,
    type,
    additionalCompensationDefaults,
    rowIdToCfIdMap,
  ]);

  // Seed the attachments store from the offer GET on load/offer-change (CORE-4803),
  // but preserve it for the same offer so session add/remove survive refetch (CORE-4796).
  const seededOfferIdRef = useRef<number | string | null>(null);
  useEffect(() => {
    if (currentOffer?.id) {
      setOfferId(currentOffer?.id || 0);
    }
    // Wait for the fetch to settle before seeding; seeding stale cache made deleted files reappear (CORE-4800).
    if (isOfferFetching) return;
    const seed = nextSeededAttachments(
      currentOffer?.id,
      currentOffer?.attachments,
      offerAttachments,
      seededOfferIdRef.current
    );
    if (seed) {
      setOfferAttachments(seed.attachments);
      seededOfferIdRef.current = seed.offerId;
    }
  }, [
    currentOffer,
    offerAttachments,
    isOfferFetching,
    setOfferId,
    setOfferAttachments,
  ]);

  // Close any date-picker calendar left open when the step changes, so it can't float
  // over the next step's content (CORE-4852). Doesn't hide the shared portal, so the
  // next step's own date fields (e.g. the inline empty-tokens form) still open.
  useEffect(() => {
    closeStaleDatePicker();
  }, [currentStep?.id]);

  // Determine overall loading state
  const isLoading = useMemo(() => {
    const loadingStates = [
      isCustomFieldsLoading,
      isCurrenciesLoading,
      isOfficesLoading,
      isDepartmentsLoading,
      isOfferFetching,
      isApplicantFetching,
    ];

    return loadingStates.some((loading) => loading);
  }, [
    isCustomFieldsLoading,
    isCurrenciesLoading,
    isOfficesLoading,
    isDepartmentsLoading,
    isOfferFetching,
    isApplicantFetching,
  ]);

  // Generate form config only when data is ready
  const { detailsFormConfig = [] } = useOfferInitiationConfig({
    tabIndex: 0,
    offerStaticFields,
    offerData: currentOffer,
    formRef: childRefs?.OFFER_DETAILS_COMP,
    isOfferV1: true,
  });

  // Show spinner while loading required data
  if (isLoading) {
    return (
      <Flex
        h="100%"
        w="100%"
        justifyContent="center"
        alignItems="center"
        minH="400px"
      >
        <Spinner color="primary-600" />
      </Flex>
    );
  }

  return (
    <>
      <Box
        display={currentStep?.id === 'offerDetails' ? 'block' : 'none'}
        {...(multiDocsEnabled && { maxW: '650px', mx: 'auto' })}
      >
        <OfferDetails
          ref={childRefs?.OFFER_DETAILS_COMP}
          initialValues={initialValues?.details || {}}
          detailsFormConfig={detailsFormConfig}
          detailsFormRef={detailsFormRef}
          reapprovalHighlightTokens={reapprovalHighlightTokens}
        />
      </Box>
      <Box
        display={currentStep?.id === 'offerLetterTemplate' ? 'flex' : 'none'}
        height="100%"
      >
        <OfferLetterTemplate
          ref={childRefs?.OFFER_LETTER_TEMPLATE_COMP}
          initialValues={initialValues?.templates || {}}
          detailsFormRef={detailsFormRef}
          detailsFormConfig={detailsFormConfig}
          currentOffer={currentOffer}
          onTemplateSelect={onTemplateSelect}
          currentStepID={currentStep?.id}
          initialDepartmentId={initialValues?.details?.ats_department_id}
          initialOfficeId={initialValues?.details?.ats_office_id}
          sendMode={sendMode}
          existingFormValues={initialValues?.details || {}}
          validationValues={validationValues}
          onEditOffer={onEditOffer}
        />
      </Box>
      <Box
        display={currentStep?.id === 'offerApproval' ? 'block' : 'none'}
        {...(multiDocsEnabled && { maxW: '650px', mx: 'auto' })}
      >
        <OfferApproval
          ref={childRefs?.OFFER_APPROVAL_COMP}
          type={type}
          currentOffer={currentOffer}
        />
      </Box>
      <Box
        display={currentStep?.id === 'offerEmail' ? 'block' : 'none'}
        {...(multiDocsEnabled && { maxW: '650px', mx: 'auto' })}
      >
        {signatureType && signatureType !== 'kula' ? (
          <EsignEmailForm
            ref={childRefs?.OFFER_EMAIL_COMP}
            integrationType={
              (
                {
                  docusign: 'docusign',
                  pandadoc: 'pandadocs',
                  adobe: 'adobe',
                } as const
              )[signatureType]
            }
            isAdmin={false}
          />
        ) : (
          <OfferEmail
            ref={childRefs?.OFFER_EMAIL_COMP}
            type={type}
            currentOffer={currentOffer}
            selectSignatureType={signatureType}
          />
        )}
      </Box>
    </>
  );
};
